import { Injectable, type OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiErrorNotifierService, type ApiErrorContext } from './api-error-notifier.service';
import { DialogMessageService } from './dialog-message.service';
import { ApplicationInsightsService } from './application-insights.service';

/**
 * Global error handler that subscribes to API errors and displays them to the user.
 * This service decouples the API service from the UI notification logic.
 *
 * Initialize this service in your app.component.ts or in a core module.
 *
 * @example
 * // In app.component.ts
 * constructor(
 *   private globalErrorHandler: GlobalApiErrorHandlerService
 * ) {
 *   this.globalErrorHandler.initialize();
 * }
 */
@Injectable({
  providedIn: 'root'
})
export class GlobalApiErrorHandlerService implements OnDestroy {
  private _subscription?: Subscription;

  constructor(
    private readonly _errorNotifier: ApiErrorNotifierService,
    private readonly _dialogMessage: DialogMessageService,
    private readonly _appInsights: ApplicationInsightsService,
  ) {}

  /**
   * Initializes the global error handler.
   * Call this method once in your app initialization (e.g., app.component.ts constructor).
   */
  public initialize(): void {
    if (this._subscription) {
      console.warn('GlobalApiErrorHandlerService already initialized');
      return;
    }

    this._subscription = this._errorNotifier.errors$.subscribe({
      next: (context: ApiErrorContext) => this._handleError(context),
      error: (err) => console.error('Error in global error handler:', err)
    });
  }

  /**
   * Handles an API error context by displaying it to the user if necessary.
   */
  private _handleError(context: ApiErrorContext): void {
    this._trackError(context);

    // Log per debugging (optional, già loggato in ApiService)
    if (!context.shouldNotify) {
      return;
    }

    // Mostra errore all'utente
    this._dialogMessage.showResponseError(context.error);
  }

  /**
   * Tracks the error for analytics/monitoring purposes.
   */
  private _trackError(context: ApiErrorContext): void {
    const properties: Record<string, unknown> = {
      errorType: context.errorType,
      statusCode: context.statusCode ?? 'unknown',
      url: context.url ?? context.error.url ?? 'unknown',
      message: context.error.message ?? 'Unknown API error',
      shouldNotify: context.shouldNotify,
      timestamp: context.timestamp.toISOString(),
    };

    if (context.error.stacktrace) {
      properties['stacktrace'] = context.error.stacktrace;
    }

    this._appInsights.trackEvent('API Error', properties, this._buildMeasurements(context));

    if (this._shouldTrackException(context)) {
      const exception = new Error(context.error.message ?? `API error: ${context.errorType}`);

      if (context.error.stacktrace) {
        exception.stack = context.error.stacktrace;
      }

      this._appInsights.trackException(exception, 3, properties);
    }
  }

  private _buildMeasurements(context: ApiErrorContext): Record<string, number> | undefined {
    if (context.statusCode == null) {
      return undefined;
    }

    return {
      statusCode: context.statusCode,
    };
  }

  private _shouldTrackException(context: ApiErrorContext): boolean {
    return context.statusCode == null || context.statusCode === 0 || context.statusCode >= 500;
  }

  /**
   * Cleanup when service is destroyed.
   */
  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }
}
